<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="shipping-update-form">
          <div class="form-header">
            <h3>Update Shipping Address</h3>
            <p>Please provide your shipping address to complete your order.</p>
            <div class="order-info">
              <strong>Order #{{ orderNumber }}</strong>
              <span class="payment-method">{{ paymentMethod }}</span>
            </div>
          </div>

          <Form @submit="updateShippingAddress" v-slot="{ errors }">
            <div class="form-content">
              <div class="row">
                <div class="form-group col-md-6">
                  <Field
                    type="text"
                    rules="required"
                    v-model="shippingForm.first_name"
                    name="first_name"
                    class="form-control"
                    placeholder="First Name *"
                  />
                  <ErrorMessage name="first_name" class="error-message" />
                </div>
                <div class="form-group col-md-6">
                  <Field
                    type="text"
                    rules="required"
                    v-model="shippingForm.last_name"
                    name="last_name"
                    class="form-control"
                    placeholder="Last Name *"
                  />
                  <ErrorMessage name="last_name" class="error-message" />
                </div>
              </div>

              <div class="row">
                <div class="form-group col-12">
                  <Field
                    type="text"
                    rules="required"
                    v-model="shippingForm.address1"
                    name="address1"
                    class="form-control"
                    placeholder="Address Line 1 *"
                  />
                  <ErrorMessage name="address1" class="error-message" />
                </div>
              </div>

              <div class="row">
                <div class="form-group col-12">
                  <Field
                    type="text"
                    v-model="shippingForm.address2"
                    name="address2"
                    class="form-control"
                    placeholder="Address Line 2 (Optional)"
                  />
                </div>
              </div>

              <div class="row">
                <div class="form-group col-md-4">
                  <Field
                    type="text"
                    rules="required"
                    v-model="shippingForm.city"
                    name="city"
                    class="form-control"
                    placeholder="City *"
                  />
                  <ErrorMessage name="city" class="error-message" />
                </div>
                <div class="row">
                <div class="form-group col-md-6">
                  <Field
                    as="select"
                    rules="required"
                    v-model="shippingForm.country"
                    name="country"
                    class="form-control"
                    @change="onCountryChange"
                  >
                    <option value="">Select Country *</option>
                    <option v-for="country in countries" :key="country.id" :value="country.id">
                      {{ country.name }}
                    </option>
                  </Field>
                  <ErrorMessage name="country" class="error-message" />
                </div>
                 <div class="form-group col-md-4">
                  <Field
                    as="select"
                    rules="required"
                    v-model="shippingForm.state"
                    name="state"
                    class="form-control"
                  >
                    <option value="">Select State/Province *</option>
                    <option v-for="state in states" :key="state.id" :value="state.id">
                      {{ state.name }}
                    </option>
                  </Field>
                  <ErrorMessage name="state" class="error-message" />
                </div>
                
              </div>
               
                <div class="form-group col-md-4">
                  <Field
                    type="text"
                    rules="required"
                    v-model="shippingForm.zip_code"
                    name="zip_code"
                    class="form-control"
                    placeholder="Zip Code *"
                  />
                  <ErrorMessage name="zip_code" class="error-message" />
                </div>
                <div class="form-group col-md-6">
                  <Field
                    type="tel"
                    rules="required"
                    v-model="shippingForm.mobile"
                    name="mobile"
                    class="form-control"
                    placeholder="Phone Number *"
                  />
                  <ErrorMessage name="phone" class="error-message" />
                </div>
              </div>

              

              <div class="form-actions">
                <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                  <span v-if="isSubmitting">Updating...</span>
                  <span v-else>Update & Complete Order</span>
                </button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form, Field, ErrorMessage } from 'vee-validate';
import axios from 'axios';

const route = useRoute();
const router = useRouter();

const orderId = ref(route.query.order_id);
const orderNumber = ref('');
const paymentMethod = ref(route.query.payment_method || 'Express Checkout');
const isSubmitting = ref(false);

const countries = ref([]);
const states = ref([]);

const shippingForm = ref({
  first_name: '',
  last_name: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  mobile: ''
});

const props = defineProps(['baseURL']);

onMounted(async () => {
  await loadCountries();
  await loadOrderDetails();
});

const loadCountries = async () => {
  try {
    const response = await axios.get(props.baseURL + 'countries');
    countries.value = response.data.countries;
  } catch (error) {
    console.error('Error loading countries:', error);
  }
};

const loadOrderDetails = async () => {
  try {
    const response = await axios.get(props.baseURL + `orders/${orderId.value}`);
    if (response.data.success) {
      orderNumber.value = response.data.order.order_number;
    }
  } catch (error) {
    console.error('Error loading order details:', error);
  }
};

const onCountryChange = async () => {
  if (shippingForm.value.country) {
    try {
      const response = await axios.get(props.baseURL + `states/${shippingForm.value.country}`);
      states.value = response.data.states;
      shippingForm.value.state = ''; // Reset state selection
    } catch (error) {
      console.error('Error loading states:', error);
    }
  }
};

const updateShippingAddress = async () => {
  isSubmitting.value = true;
  
  try {
    const response = await axios.post(props.baseURL + 'express-checkout/update-shipping', {
      order_id: orderId.value,
      shipping_address: shippingForm.value
    });

    if (response.data.success) {
      swal({
        icon: 'success',
        title: 'Shipping Address Updated!',
        text: 'Your order is now complete.',
        timer: 2000
      });

      // Redirect to success page
      router.push({
        name: 'PaymentSuccess',
        query: { 
          order_id: orderId.value, 
          payment_method: paymentMethod.value 
        }
      });
    } else {
      throw new Error(response.data.message || 'Failed to update shipping address');
    }
  } catch (error) {
    swal({
      icon: 'error',
      title: 'Update Failed',
      text: error.response?.data?.message || error.message || 'Failed to update shipping address'
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.shipping-update-form {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  margin: 20px 0;
}

.form-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.form-header h3 {
  color: #333;
  margin-bottom: 10px;
}

.form-header p {
  color: #666;
  margin-bottom: 15px;
}

.order-info {
  background: #f8f9fa;
  padding: 10px 15px;
  border-radius: 5px;
  display: inline-block;
}

.order-info strong {
  color: #333;
  margin-right: 10px;
}

.payment-method {
  background: #28a745;
  color: white;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  text-transform: uppercase;
}

.form-content {
  max-width: 600px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-control {
  height: 45px;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px 15px;
  font-size: 14px;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.form-actions {
  text-align: center;
  margin-top: 30px;
}

.btn {
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>